import type {
  ApiErrorResponse,
  AuthStateResponse,
  CreateTokenResponse,
  ListPasskeysResponse,
  ListPastesResponse,
  ListTokensResponse,
  ListVersionsResponse,
  MeResponse,
  PasteDetail,
  VersionDetail,
  WritePasteRequest,
  WritePasteResponse,
} from '../shared/types'
import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser'

export class ApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

async function request<T>(input: string, init?: RequestInit): Promise<T> {
  let response: Response
  try {
    response = await fetch(input, init)
  } catch {
    throw new ApiError('Could not reach the kitchen — check your connection', 0)
  }

  const body = await response.text()
  let parsed: unknown = null
  if (body) {
    try {
      parsed = JSON.parse(body)
    } catch {
      throw new ApiError(`Unexpected response from the server (${response.status})`, response.status)
    }
  }

  if (!response.ok) {
    const message = (parsed as ApiErrorResponse | null)?.error ?? `Request failed (${response.status})`
    throw new ApiError(message, response.status)
  }

  return parsed as T
}

function jsonInit(method: string, payload: unknown): RequestInit {
  return { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload) }
}

export function fetchMe(): Promise<MeResponse> {
  return request<MeResponse>('/api/me')
}

/** With `query`, searches titles and bodies; without, lists everything. */
export function listPastes(query?: string): Promise<ListPastesResponse> {
  const suffix = query ? `?q=${encodeURIComponent(query)}` : ''
  return request<ListPastesResponse>(`/api/pastes${suffix}`)
}

export function fetchPaste(id: string): Promise<PasteDetail> {
  return request<PasteDetail>(`/api/pastes/${encodeURIComponent(id)}`)
}

export function createPaste(input: WritePasteRequest): Promise<WritePasteResponse> {
  return request<WritePasteResponse>('/api/pastes', jsonInit('POST', input))
}

export function updatePaste(id: string, input: WritePasteRequest): Promise<WritePasteResponse> {
  return request<WritePasteResponse>(`/api/pastes/${encodeURIComponent(id)}`, jsonInit('PUT', input))
}

export function deletePaste(id: string): Promise<void> {
  return request<void>(`/api/pastes/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export function listVersions(id: string): Promise<ListVersionsResponse> {
  return request<ListVersionsResponse>(`/api/pastes/${encodeURIComponent(id)}/versions`)
}

export function fetchVersion(id: string, versionId: number): Promise<VersionDetail> {
  return request<VersionDetail>(`/api/pastes/${encodeURIComponent(id)}/versions/${versionId}`)
}

export function restoreVersion(id: string, versionId: number): Promise<void> {
  return request<void>(`/api/pastes/${encodeURIComponent(id)}/versions/${versionId}/restore`, {
    method: 'POST',
  })
}

export function listTokens(): Promise<ListTokensResponse> {
  return request<ListTokensResponse>('/api/tokens')
}

export function createToken(name: string): Promise<CreateTokenResponse> {
  return request<CreateTokenResponse>('/api/tokens', jsonInit('POST', { name }))
}

export function deleteToken(id: string): Promise<void> {
  return request<void>(`/api/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export function fetchAuthState(): Promise<AuthStateResponse> {
  return request<AuthStateResponse>('/api/auth/state')
}

export function registrationOptions(input: {
  bootstrapCode?: string
  label?: string
}): Promise<PublicKeyCredentialCreationOptionsJSON> {
  return request<PublicKeyCredentialCreationOptionsJSON>(
    '/api/auth/register/options',
    jsonInit('POST', input),
  )
}

export function verifyRegistration(input: {
  response: RegistrationResponseJSON
  name: string
  label?: string
}): Promise<MeResponse> {
  return request<MeResponse>('/api/auth/register/verify', jsonInit('POST', input))
}

export function authenticationOptions(): Promise<PublicKeyCredentialRequestOptionsJSON> {
  return request<PublicKeyCredentialRequestOptionsJSON>(
    '/api/auth/login/options',
    jsonInit('POST', {}),
  )
}

export function verifyAuthentication(response: AuthenticationResponseJSON): Promise<MeResponse> {
  return request<MeResponse>('/api/auth/login/verify', jsonInit('POST', { response }))
}

export function listPasskeys(): Promise<ListPasskeysResponse> {
  return request<ListPasskeysResponse>('/api/passkeys')
}

export function deletePasskey(id: string): Promise<void> {
  return request<void>(`/api/passkeys/${encodeURIComponent(id)}`, { method: 'DELETE' })
}
